import {
  useCreateHeliosDataTablesRecordMutation,
  useUpdateHeliosDataTablesRecordMutation,
  useDeleteHeliosDataRecordMutation,
} from 'store/services/helios/dataHelios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { processRowData } from 'utils/helios/row';
import { invokeRNMessage } from 'utils/common';
import { trackFormSubmitSuccess, trackFormSubmitError } from 'utils/posthog/metrics';
import { ReactNativeMessageType, type DataSchema } from 'types';
import type { TABLE_CELL_TYPE } from 'types/enums';
import type { HeliosColumnsCellDataForBe, HeliosColumnsCellData } from 'types/helios/agendaListing';
import type { HeliosRecordDataTablesRes } from 'types/dataHelios';
import type { SaveAllRowsResult } from 'types/helios/record';

interface UseHeliosRowActionsProps {
  templateViewId: string;
  templateId: string;
  dataSchema: DataSchema;
  handleCancelEditRow: (rowId: string) => void;
  refetch: (result?: SaveAllRowsResult) => void;
  data: HeliosRecordDataTablesRes | undefined;
  transformedData: HeliosColumnsCellData[];
}

export const useHeliosRowActions = ({
  templateViewId,
  templateId,
  dataSchema,
  handleCancelEditRow,
  refetch,
  data,
  transformedData,
}: UseHeliosRowActionsProps) => {
  const { t } = useTranslation();

  const [idsForEdit, setIdsForEdit] = useState<string[]>([]);
  const [idsForMobileInRowEdit, setIdsForMobileInRowEdit] = useState<string[]>([]);
  const [recordToDelete, setRecordToDelete] = useState<string>('');
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);

  const [createHeliosDataTablesRecord, { isLoading: isCreateLoading }] = useCreateHeliosDataTablesRecordMutation();
  const [updateHeliosDataTablesRecord] = useUpdateHeliosDataTablesRecordMutation();
  const [deleteHeliosDataRecord] = useDeleteHeliosDataRecordMutation();

  const isEditable = useMemo(() => idsForEdit.length > 0, [idsForEdit]);

  const handleCancelEditRowWithMobile = useCallback(
    (rowId: string) => {
      if (idsForMobileInRowEdit.length === 1) {
        invokeRNMessage(ReactNativeMessageType.SetEditMode, { isEditMode: false });
      }

      setIdsForMobileInRowEdit((prev) => prev.filter((id) => id !== rowId));
      handleCancelEditRow(rowId);
    },
    [handleCancelEditRow, idsForMobileInRowEdit.length]
  );

  useEffect(() => {
    if (!data?.data) return;

    const dataMap = Object.fromEntries(transformedData.map((item) => [item.id, item.identifier]));

    const editableRowIds = data.data
      .filter((record) => record.actions.editInRow)
      .map((record) => dataMap[record.recordNumber?.toString()])
      .filter(Boolean);

    if (editableRowIds.length > 0) {
      setIdsForEdit((prev) => [...prev, ...editableRowIds]);
    }
  }, [data, transformedData]);

  const handleSaveCreatedRow = useCallback(
    async (row: Record<string, { value: string | number | boolean | object; type: TABLE_CELL_TYPE }>) => {
      const rowId = row.id.value as string;

      try {
        const columns = processRowData({ row, dataSchema });
        const res = await createHeliosDataTablesRecord({
          templateId,
          columns: columns as HeliosColumnsCellDataForBe[],
        });

        if (!('error' in res)) {
          toast.success(t('helios.listing.success.createRow'));
          handleCancelEditRow(rowId);
          setIdsForEdit([]);
          refetch();
          try {
            trackFormSubmitSuccess({ form: 'helios_listing_inline', process: 'record', step: 'create_row_success', area: 'listing_table' });
          } catch {
            /* noop */
          }
        }
      } catch (e) {
        toast.error(t('common.error'));
        try {
          trackFormSubmitError({
            form: 'helios_listing_inline',
            process: 'record',
            step: 'create_row_error',
            error: (e as Error)?.message || 'unknown_error',
            area: 'listing_table',
          });
        } catch {
          /* noop */
        }
      }
    },
    [createHeliosDataTablesRecord, dataSchema, handleCancelEditRow, refetch, t, templateId]
  );

  const handleSaveEditedRow = useCallback(
    async (row: Record<string, { value: string | number | boolean | object; type: TABLE_CELL_TYPE }>) => {
      const rowId = row.id.value as string;

      try {
        const columns = processRowData({ row, dataSchema });
        const res = await updateHeliosDataTablesRecord({
          templateId: templateViewId,
          recordNumber: Number(rowId),
          columns: columns as HeliosColumnsCellDataForBe[],
        });

        if (!('error' in res)) {
          toast.success(t('helios.listing.success.editRow'));
          handleCancelEditRow(rowId);
          setIdsForEdit([]);
          setIdsForMobileInRowEdit([]);
          invokeRNMessage(ReactNativeMessageType.SetEditMode, { isEditMode: false });
          refetch();
          try {
            trackFormSubmitSuccess({ form: 'helios_listing_inline', process: 'record', step: 'edit_row_success', area: 'listing_table' });
          } catch {
            /* noop */
          }
        }
      } catch (e) {
        toast.error(t('common.error'));
        try {
          trackFormSubmitError({
            form: 'helios_listing_inline',
            process: 'record',
            step: 'edit_row_error',
            error: (e as Error)?.message || 'unknown_error',
            area: 'listing_table',
          });
        } catch {
          /* noop */
        }
      }
    },
    [dataSchema, updateHeliosDataTablesRecord, templateViewId, handleCancelEditRow, refetch, t]
  );

  const handleDeleteRecord = useCallback(async () => {
    setIsDeleteInProgress(true);
    try {
      const record = data?.data?.find((d) => d.recordNumber.toString() === recordToDelete);
      if (!record?.recordNumber) {
        toast.error(t('common.error'));
        setIsDeleteInProgress(false);
        return;
      }
      const res = await deleteHeliosDataRecord({
        templateId: templateViewId,
        recordNumber: record.recordNumber,
        masterRecordNumber: record.masterRecordNumber,
      });

      if (!('error' in res)) {
        toast.success(t('helios.listing.success.deleteRow'));
        refetch();
      }
    } catch (error) {
      toast.error(t('user.delete.error'));
      // eslint-disable-next-line no-console
      console.error(`Error: ${error}. When deleting record: ${recordToDelete}`);
    }
    setIsDeleteInProgress(false);
  }, [deleteHeliosDataRecord, templateViewId, recordToDelete, t, refetch, data]);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteInProgress(false);
  }, []);

  return {
    idsForEdit,
    idsForMobileInRowEdit,
    setIdsForEdit,
    setIdsForMobileInRowEdit,
    recordToDelete,
    setRecordToDelete,
    isDeleteInProgress,
    isEditable,
    isCreateLoading,
    handleSaveCreatedRow,
    handleSaveEditedRow,
    handleDeleteRecord,
    handleDeleteModalClose,
    handleCancelEditRowWithMobile,
  };
};
